/**
 * Alpine component powering the stacked-card scroll effect: cards stick, shrink and fade once
 */
const prefersReducedMotion = () =>
	typeof window !== 'undefined'
	&& typeof window.matchMedia === 'function'
	&& window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hasIntersectionObserver = () =>
	typeof window !== 'undefined' && 'IntersectionObserver' in window;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default () => ({
	observer: null,
	observerCallback: null,
	scrollHandler: null,
	resizeHandler: null,
	destroyHandler: null,
	ticking: false,
	items: [],
	cardHeight: 0,
	cardTop: 0,
	gap: 0,
	minOpacity: 0,

	// Set up observers, listeners and initial measurements
	init() {
		if (!hasIntersectionObserver() || prefersReducedMotion()) return;

		this.items = Array.from(this.$el.getElementsByClassName('js-stack-cards__item'));
		if (!this.items.length) return;

		this.configureFromDataset();
		this.measure();
		this.animate();

		this.observerCallback = this.handleIntersection.bind(this);
		this.observer = new IntersectionObserver(this.observerCallback, { threshold: 0.15 });
		this.observer.observe(this.$el);

		this.resizeHandler = () => {
			this.resetStyles();
			this.measure();
			this.animate();
		};

		window.addEventListener('resize', this.resizeHandler);
		this.destroyHandler = () => this.destroy();
		this.$el.addEventListener('alpine:destroy', this.destroyHandler);
	},

	// Read configuration from data attributes on the root element
	configureFromDataset() {
		const { stackGap, stackOpacityMin } = this.$el.dataset;
		this.gap = clamp(parseFloat(stackGap), 0, 400) || 20;
		if (stackOpacityMin !== undefined) {
			this.minOpacity = clamp(parseFloat(stackOpacityMin), 0, 1);
		}
	},

	// IntersectionObserver callback toggles scroll listener based on visibility
	handleIntersection(entries) {
		if (!entries.length) return;
		if (entries[0].isIntersecting) {
			this.addScrollListener();
		} else {
			this.removeScrollListener();
		}
	},

	// Register a throttled scroll listener for smooth animations
	addScrollListener() {
		if (this.scrollHandler) return;
		this.scrollHandler = () => {
			if (this.ticking) return;
			this.ticking = true;
			window.requestAnimationFrame(() => {
				this.animate();
				this.ticking = false;
			});
		};
		window.addEventListener('scroll', this.scrollHandler, { passive: true });
	},

	// Remove the scroll listener when the section leaves the viewport
	removeScrollListener() {
		if (!this.scrollHandler) return;
		window.removeEventListener('scroll', this.scrollHandler);
		this.scrollHandler = null;
	},

	// Capture latest geometry information used for transforms
	measure() {
		const firstCard = this.items[0];
		this.cardHeight = firstCard.getBoundingClientRect().height;
		this.cardTop = this.$el.getBoundingClientRect().top + window.scrollY;
	},

	// Apply transforms/opacity values based on scroll progress
	animate() {
		if (!this.cardHeight) return;

		const top = this.$el.getBoundingClientRect().top;
		const pinnedOffset = Math.max(0, -top);
		const overlapThreshold = this.cardHeight * 0.2; // shrink once ~80% is covered

		this.items.forEach((item, index) => {
			const translate = this.gap * index;
			const scrolling = pinnedOffset - index * (this.cardHeight + this.gap);

			if (scrolling > overlapThreshold) {
				const overlapProgress = scrolling - overlapThreshold;
				const scale = clamp((this.cardHeight - overlapProgress * 0.05) / this.cardHeight, 0, 1);
				const opacityProgress = clamp(overlapProgress / this.cardHeight, 0, 1);
				const opacity = 1 - (1 - this.minOpacity) * opacityProgress;
				this.applyStyles(item, translate, scale, opacity);
			} else {
				this.applyStyles(item, translate, 1, 1);
			}
		});
	},

	// Helper to keep inline style assignments consistent
	applyStyles(item, translate, scale, opacity) {
		item.style.transform = `translateY(${translate}px) scale(${scale})`;
		const content = item.querySelector('.stacked-card__content, .stack-cards__content');
		if (content) {
			content.style.opacity = opacity.toFixed(3);
		}
	},

	// Clean up inline styles before recalculating measurements
	resetStyles() {
		this.items.forEach((item) => {
			item.style.removeProperty('transform');
			const content = item.querySelector('.stacked-card__content, .stack-cards__content');
			if (content) {
				content.style.removeProperty('opacity');
			}
		});
	},

	// Tear down all observers and listeners on Alpine destroy
	destroy() {
		this.removeScrollListener();

		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}

		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
			this.resizeHandler = null;
		}
		if (this.destroyHandler) {
			this.$el.removeEventListener('alpine:destroy', this.destroyHandler);
			this.destroyHandler = null;
		}
	}
});
